import React, { useState, useMemo } from 'react';
import Header from './Header';
import AnimationPreview from './AnimationPreview';
import ControlsPanel from './ControlsPanel';
import CodePanel from './CodePanel';
import { animationCategories, defaultAnimationSettings, previewObjects } from '../data/mock';
import { cn } from '../lib/utils';
import { Play, Settings2 } from 'lucide-react';
import { Button } from './ui/button';
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "./ui/sheet";

const PlaygroundPage = ({ onBack }) => {
  const [selectedCategoryId, setSelectedCategoryId] = useState('basic');
  const [selectedAnimationId, setSelectedAnimationId] = useState('scale-up');
  const [selectedVariation, setSelectedVariation] = useState('scale-up-center');

  const [settings, setSettings] = useState(defaultAnimationSettings);
  const [previewObject, setPreviewObject] = useState(previewObjects[0]);
  const [favorites, setFavorites] = useState([]);
  const [showCodePanel, setShowCodePanel] = useState(false);
  const [activeCodeTab, setActiveCodeTab] = useState('html-css');

  const handleOpenCodePanel = (tab = 'html-css') => {
    setActiveCodeTab(tab);
    setShowCodePanel(true);
  };

  const selectedCategory = useMemo(() =>
    animationCategories.find(c => c.id === selectedCategoryId) || animationCategories[0]
    , [selectedCategoryId]);

  const selectedAnimation = useMemo(() =>
    selectedCategory.animations.find(a => a.id === selectedAnimationId) || selectedCategory.animations[0]
    , [selectedCategory, selectedAnimationId]);

  const isFavorite = favorites.includes(selectedVariation);

  const handleToggleFavorite = () => {
    if (isFavorite) {
      setFavorites(favorites.filter(f => f !== selectedVariation));
    } else {
      setFavorites([...favorites, selectedVariation]);
    }
  };

  const handleCategoryChange = (id) => {
    const cat = animationCategories.find(c => c.id === id);
    setSelectedCategoryId(id);
    setSelectedAnimationId(cat.animations[0].id);
    setSelectedVariation(cat.animations[0].variations[0] || cat.animations[0].id);
  };

  const handleAnimationChange = (id) => {
    const anim = selectedCategory.animations.find(a => a.id === id);
    setSelectedAnimationId(id);
    setSelectedVariation(anim.variations[0] || anim.id);
  };

  return (
    <div className="h-screen flex flex-col bg-[#0F172A] text-slate-100 font-sans selection:bg-violet-500/30">
      <Header
        categories={animationCategories}
        selectedCategoryId={selectedCategoryId}
        onSelectCategory={handleCategoryChange}
        favorites={favorites}
        onDownload={() => handleOpenCodePanel('html-css')}
      />

      {/* Main Responsive Container */}
      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden relative">

        {/* Mobile Control Trigger - Floating Button */}
        <div className="lg:hidden absolute bottom-8 right-8 z-50">
          <Sheet>
            <SheetTrigger asChild>
              <Button className="w-14 h-14 rounded-full bg-violet-600 shadow-2xl shadow-violet-500/50 flex items-center justify-center group active:scale-95 transition-all">
                <Settings2 className="w-6 h-6 text-white group-hover:rotate-90 transition-transform duration-500" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="p-0 bg-[#0F172A] border-white/10 w-[300px]">
              <ControlsPanel
                settings={settings}
                onSettingsChange={setSettings}
                previewObject={previewObject}
                onPreviewObjectChange={setPreviewObject}
              />
            </SheetContent>
          </Sheet>
        </div>

        {/* Left Sidebar: Controls (Desktop Only) */}
        <div className="hidden lg:block border-r border-white/5">
          <ControlsPanel
            settings={settings}
            onSettingsChange={setSettings}
            previewObject={previewObject}
            onPreviewObjectChange={setPreviewObject}
          />
        </div>

        {/* Main Workspace */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Sub-nav: Animation Types */}
          <div className="bg-[#1E293B]/40 border-b border-white/5 px-4 md:px-8 py-4 md:py-6 flex items-center gap-6 md:gap-10 overflow-x-auto no-scrollbar relative z-10 backdrop-blur-xl shrink-0">
            {selectedCategory.animations.map((anim) => (
              <button
                key={anim.id}
                onClick={() => handleAnimationChange(anim.id)}
                className="flex flex-col items-center gap-2 md:gap-3 group min-w-[80px] md:min-w-[100px] transition-all duration-500 hover:translate-y-[-4px]"
              >
                <div className={cn(
                  "w-12 h-12 md:w-16 md:h-16 rounded-[1rem] md:rounded-[1.25rem] border-2 flex items-center justify-center transition-all duration-700 transform",
                  selectedAnimationId === anim.id
                    ? "border-violet-500 bg-gradient-to-br from-violet-500 to-indigo-600 shadow-[0_0_30px_rgba(139,92,246,0.3)] rotate-[8deg] scale-110"
                    : "border-white/5 bg-white/[0.03] text-slate-500 group-hover:border-violet-500/30 group-hover:rotate-[4deg] group-hover:bg-white/[0.07]"
                )}>
                  <div className="flex flex-col items-center">
                    <span className={cn(
                      "text-[8px] md:text-[10px] font-black uppercase tracking-tighter transition-all",
                      selectedAnimationId === anim.id ? "text-white" : "text-slate-500 group-hover:text-slate-300"
                    )}>
                      {anim.name.substring(0, 1)}
                    </span>
                    <span className={cn(
                      "text-[8px] md:text-[10px] font-black uppercase tracking-tighter opacity-50",
                      selectedAnimationId === anim.id ? "text-white" : "text-slate-500"
                    )}>
                      {anim.name.substring(1, 2)}
                    </span>
                  </div>
                </div>
                <span className={cn(
                  "text-[8px] md:text-[10px] font-black uppercase tracking-[0.2em] transition-colors duration-300 truncate w-full text-center",
                  selectedAnimationId === anim.id ? "text-violet-400" : "text-slate-500 group-hover:text-slate-200"
                )}>
                  {anim.name}
                </span>
              </button>
            ))}
          </div>

          {/* Variation Bar */}
          <div className="bg-[#0F172A] border-b border-white/5 px-4 md:px-8 py-2.5 md:py-3.5 flex items-center gap-3 md:gap-4 overflow-x-auto no-scrollbar shrink-0">
            {selectedAnimation.variations.map((v) => (
              <button
                key={v}
                onClick={() => setSelectedVariation(v)}
                className={cn(
                  "px-3 md:px-5 py-1.5 md:py-2 rounded-lg md:rounded-xl text-[8px] md:text-[10px] font-black uppercase tracking-widest transition-all duration-500 border relative overflow-hidden group/var shrink-0",
                  selectedVariation === v
                    ? "bg-violet-600/10 border-violet-500/50 text-violet-100 shadow-[0_0_20px_rgba(139,92,246,0.1)]"
                    : "bg-white/[0.02] border-white/[0.05] text-slate-500 hover:text-slate-200 hover:border-white/20"
                )}
              >
                {selectedVariation === v && (
                  <div className="absolute inset-0 bg-violet-500 opacity-10 animate-pulse" />
                )}
                <span className="relative z-10">{v}</span>
              </button>
            ))}
          </div>

          <div className="flex-1 relative bg-[#111827]">
            {/* Subtle Grid Background */}
            <div className="absolute inset-0 pointer-events-none opacity-[0.03]">
              <div className="absolute inset-0" style={{
                backgroundImage: `linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)`,
                backgroundSize: '40px 40px'
              }} />
            </div>

            <AnimationPreview
              animationName={selectedVariation}
              settings={settings}
              previewObject={previewObject}
              onToggleFavorite={handleToggleFavorite}
              isFavorite={isFavorite}
              onOpenCodePanel={handleOpenCodePanel}
            />
          </div>
        </div>
      </div>

      {/* Code Panel Modal */}
      <CodePanel
        isOpen={showCodePanel}
        onClose={() => setShowCodePanel(false)}
        animationName={selectedVariation}
        settings={settings}
        initialTab={activeCodeTab}
      />
    </div>
  );
};

export default PlaygroundPage;
